/*
 * Proprietary and Confidential
 *
 * Copyright (c) [2018] -  [] Marcelo H. Krebber - Munich, London 2018
 * All Rights Reserved.
 *
 * Dissemination or reproduction of this file [secondary-navigation.js] or parts within
 * via any medium is strictly forbidden unless prior written permission is obtained
 * from <marcelo.krebber@gmail.com>
 *
 * Last modified: 22.12.19, 16:57
 */

import React, {useEffect, useState} from 'react';
import {Link} from "react-router-dom";
import {useAuthorization, useNotifications} from "../../selectors";
import {Spinner} from "../util/spinner";
import {formatLongFns, useIsMobile, useLanguage} from "../configuration/configuration";
import {PENDING_STATUS} from "../../actions";
import useFetchPageMenu from "../hooks/useFetchPageMenu";
import {ArrowRightIcon, DescriptionIcon, DigitalCardIcon, fs_smaller} from "./icons/nav-icons";
import DigitalWallet from "../dialogs/user/digital-wallet";

/**
 * supports loose menu building thru page categories
 * for first level - a sublevel must appear as 'level' ie 'level/sublevel'
 */
const SecondaryNavigation = () => {
    const [showDigitalWallet, setShowDigitalWallet] = useState(false);
    const {fetchMenuPages, fetchPublicMenuPages, pages, status} = useFetchPageMenu();
    const {isAuthorized, principalUsername} = useAuthorization();
    const Lang = useLanguage();
    const notifications = useNotifications();
    const isMobile = useIsMobile();

    useEffect(() => {
        isAuthorized ? fetchMenuPages(principalUsername) : fetchPublicMenuPages(principalUsername);
    }, [isAuthorized, notifications.page])

    // support for first level - a sublevel must appear as 'level' ie 'level/sublevel'
    // ROOT category pages are for special usage and do not appear in the menu
    const buildCategories = (pages) => {
        const categories = new Map();

        pages && pages
        .filter(page => page.category && page.category !== 'ROOT')
        .forEach(page => {
            const base = page.category.split('/', 2);
            const key = base[0];
            const bucket = categories.get(key);
            bucket ? bucket.push(page) : categories.set(key, [page]);
        })
        return categories;
    }

    // support for second level - a sublevel must appear as 'level' ie 'level/sublevel'
    const buildSubCategories = (pages) => {
        const subcategories = new Map();

        pages && pages.forEach(page => {
            const base = page.category.split('/', 2);
            const key = base.length === 1 ? base[0] : base[1];
            const bucket = subcategories.get(key);
            bucket ? bucket.push(page) : subcategories.set(key, [page]);
        })
        return subcategories;
    }

    // submenu dropdown item
    const renderDropdownSubmenu = (key, values) => {
        const isLeaf = (values.length === 1) && values[0].category.split('/').length > 1;

        return (values.length > 1) || isLeaf ? <div key={key}>
            <div className='nav-link nav-fill box-aligned py-0'>
                <span>{key}</span><ArrowRightIcon size={fs_smaller}/>
            </div>
            <ul className="dropdown-submenu">
                {renderSingleItems(values)}
            </ul>
        </div> : renderSingleItems(values)
    }

    // single navigation item
    const renderSingleItems = pages => {
        return pages ? pages.map(page => {
            return <li key={page.name} className='nav-item'>
                <Link className="dropdown-item" to={`/${principalUsername}/page/${page.name}`}>
                    <div className="d-flex align-items-center">
                        <DescriptionIcon size={fs_smaller} className="mr-1"/> {page.headline}
                    </div>
                </Link>
            </li>
        }) : new Map();
    }

    // menu items for pages within a category
    const renderMenuItems = (pages) => {
        const subcategories = buildSubCategories(pages);
        let submenu = [];
        subcategories.forEach((values, key) => {
            const item = subcategories.size > 1
                ? renderDropdownSubmenu(key, values)
                : renderSingleItems(values);
            submenu = [...submenu, item]
        })
        return submenu;
    }

    // first menu level
    const renderDesktopMenu = () => {
        const categories = buildCategories(pages);
        let menu = [];
        categories.forEach((values, key) => {
            menu = [...menu, <div key={key}>
                <a className="nav-link dropdown-toggle" data-bs-toggle="dropdown" href="#" role="button"
                   aria-expanded="false">{key}</a>
                <ul className="dropdown-menu navbar-actions-dropdown">
                    {renderMenuItems(values)}
                </ul>
            </div>]
        })
        return <div className="secondary-navigation justify-content-between rounded-bottom">
            <nav className="nav nav-pills flex-column flex-sm-row">
                {menu}
            </nav>
            <div className='nav-item mr-2'>
                <span>{formatLongFns(new Date(), 'dd MMM yyyy, EEEE')}</span>
            </div>
        </div>
    }

    const renderMobileMenu = () => {
        return <div className="secondary-navigation justify-content-between small">
            <div className='nav-item ml-1' onClick={onShowDigitalWallet}>
                <div className="d-flex align-items-center">
                    <DigitalCardIcon size={fs_smaller} className='mr-1'/>
                    <span>{Lang.nav.submenu.digitalCard.title}</span>
                </div>
            </div>
            <div className='nav-item mr-2'>
                <span>{formatLongFns(new Date(), 'dd MMM yyyy, EEEE')}</span>
            </div>
        </div>
    }

    const onShowDigitalWallet = () => {
        setShowDigitalWallet(!showDigitalWallet);
    }

    if (status === PENDING_STATUS) return <Spinner className='my-3'/>;

    return <>
        {!isMobile && renderDesktopMenu()}
        {isMobile && renderMobileMenu()}
        {showDigitalWallet && <DigitalWallet isOpen={showDigitalWallet} setIsOpen={setShowDigitalWallet}/>}
    </>
};

export default SecondaryNavigation;